// The sections and their questions - the bulk of the questionnaire.

import { useState } from "react";
import type {
  IntakeAnswerType,
  IntakeLanguage,
  IntakeOption,
  IntakeQuestion,
  IntakeRequirement,
  IntakeTemplateReserved,
} from "@duralex/shared";
import {
  ANSWER_TYPES,
  REQUIREMENTS,
  REQUIREMENT_NAMES,
  move,
  newQuestion,
  newSection,
  questionIds,
  retype,
  setRequired,
  toggleCode,
  uniqueId,
  without,
  type Draft,
  omit,
  rowKey,
  followLabel,
} from "./draft";
import { behindIn } from "../../lib/intakeLogic";
import { BehindPills, CodeChips, IdField, LabelField, RowActions } from "./fields";

interface EditorProps {
  draft: Draft;
  lang: IntakeLanguage;
  reserved: IntakeTemplateReserved;
  /** "q:<id>", "section:<id>" and kin for what the loaded copy already had:
      an id set once is keyed by answers and files, so it stays. */
  existing: Set<string>;
  /** The keys whose `lang` translation is out of date. */
  stale: Set<string>;
  /** The keys whose `lang` cell is a model's unsaved proposal. */
  ai: Set<string>;
  onChange: (draft: Draft) => void;
}

const TYPE_NAMES: Record<IntakeAnswerType, string> = {
  text: "Short text",
  longtext: "Long text",
  date: "Date",
  yesno: "Yes / no",
  choice: "One choice",
  multichoice: "Several choices",
};

function OptionsEditor({
  qid,
  options,
  lang,
  stale,
  ai,
  onChange,
}: {
  qid: string;
  options: IntakeOption[];
  lang: IntakeLanguage;
  stale: Set<string>;
  /** The keys whose `lang` cell is a model's unsaved proposal. */
  ai: Set<string>;
  onChange: (options: IntakeOption[]) => void;
}) {
  const values = options.map((o) => o.value);
  const taken = new Set(values);
  return (
    <div className="ie-options">
      {options.map((o, i) => (
        <div key={rowKey(values, i)} className="ie-option">
          <input
            type="text"
            className="cc-field ie-id"
            aria-label={`Option value ${i + 1} of ${qid}`}
            value={o.value}
            onChange={(e) =>
              onChange(options.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))
            }
          />
          <LabelField
            label={`Option ${o.value} of ${qid}`}
            text={o.label}
            lang={lang}
            stale={stale.has(`opt.${qid}.${o.value}`)}
            ai={ai.has(`opt.${qid}.${o.value}`)}
            onChange={(label) => onChange(options.map((x, j) => (j === i ? { ...x, label } : x)))}
          />
          <RowActions
            what={`option ${o.value} of ${qid}`}
            onUp={() => onChange(move(options, i, -1))}
            onDown={() => onChange(move(options, i, 1))}
            onRemove={() => onChange(without(options, i))}
            locked={options.length === 1 ? "A choice needs at least one option" : undefined}
          />
        </div>
      ))}
      <button
        type="button"
        className="cc-link-btn"
        onClick={() =>
          onChange([...options, { value: uniqueId("option", taken), label: { en: "New option" } }])
        }
      >
        Add an option
      </button>
    </div>
  );
}

function QuestionEditor({
  q,
  lang,
  draft,
  reserved,
  existing,
  stale,
  ai,
  onChange,
  onMove,
  onRemove,
}: {
  q: IntakeQuestion;
  lang: IntakeLanguage;
  draft: Draft;
  reserved: IntakeTemplateReserved;
  existing: Set<string>;
  stale: Set<string>;
  /** The keys whose `lang` cell is a model's unsaved proposal. */
  ai: Set<string>;
  onChange: (q: IntakeQuestion) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const isNew = !existing.has(`q:${q.id}`);
  // The panel is the admin's to open and close; clearing the last word of
  // a help text must not slam it shut under the caret.
  const [helpOpen, setHelpOpen] = useState(Boolean(q.help));
  const isReserved = reserved.questions.includes(q.id);
  const choice = q.type === "choice" || q.type === "multichoice";
  const help = q.help ?? { en: "" };
  return (
    <div className="ie-q">
      <div className="ie-q-head">
        <IdField
          label={`Question id (${q.id})`}
          value={q.id}
          locked={isReserved || !isNew}
          onChange={(id) => onChange({ ...q, id })}
        />
        {isReserved && (
          <span
            className="cc-pill muted"
            title="The case record, the URGENT flags or the matter read this question by name: reword it, keep its id and type."
          >
            Read by the case record
          </span>
        )}
        <select
          className="cc-field ie-select"
          aria-label={`Type of ${q.id}`}
          value={q.type}
          disabled={isReserved}
          onChange={(e) => onChange(retype(q, e.target.value as IntakeAnswerType))}
        >
          {ANSWER_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_NAMES[t]}
            </option>
          ))}
        </select>
        <select
          className="cc-field ie-select"
          aria-label={`Requirement of ${q.id}`}
          value={q.required}
          onChange={(e) => onChange(setRequired(q, e.target.value as IntakeRequirement))}
        >
          {REQUIREMENTS.map((r) => (
            <option key={r} value={r}>
              {REQUIREMENT_NAMES[r]}
            </option>
          ))}
        </select>
        <RowActions
          what={`question ${q.id}`}
          onUp={() => onMove(-1)}
          onDown={() => onMove(1)}
          onRemove={onRemove}
          locked={isReserved ? "The case record reads this question by name" : undefined}
        />
      </div>
      <LabelField
        label={`Question ${q.id}`}
        text={q.label}
        lang={lang}
        stale={stale.has(`q.${q.id}`)}
        ai={ai.has(`q.${q.id}`)}
        onChange={(label) =>
          onChange({
            ...q,
            label,
            id: isNew ? followLabel(q.id, q.label.en, label.en, "question") : q.id,
          })
        }
      />
      <details
        className="ie-help"
        open={helpOpen}
        onToggle={(e) => setHelpOpen(e.currentTarget.open)}
      >
        <summary className="ie-hint">Help text{q.help ? "" : " (none)"}</summary>
        <LabelField
          label={`Help for ${q.id}`}
          text={help}
          lang={lang}
          stale={stale.has(`q.${q.id}.help`)}
          ai={ai.has(`q.${q.id}.help`)}
          onChange={(next) => {
            // No text in any language is no help text at all, not an
            // empty one - the server refuses "".
            const any = next.en || Object.keys(next).some((k) => k !== "en");
            onChange(any ? { ...q, help: next } : omit(q, "help"));
          }}
        />
      </details>
      <CodeChips
        label={`Asked for (${q.id})`}
        value={q.appliesTo}
        categories={draft.categories}
        onToggle={(code) => onChange({ ...q, appliesTo: toggleCode(q.appliesTo, code) })}
      />
      {q.required === "yes" && (
        <div className="ie-sub">
          <label className="cc-form-check">
            <input
              type="checkbox"
              checked={Boolean(q.requiredFor)}
              onChange={(e) => {
                onChange(
                  e.target.checked
                    ? { ...q, requiredFor: [draft.categories[0]?.code ?? "ALL"] }
                    : omit(q, "requiredFor"),
                );
              }}
            />
            Required only for some case types
          </label>
          {q.requiredFor && (
            <CodeChips
              label={`Required for (${q.id})`}
              value={q.requiredFor}
              categories={draft.categories}
              onToggle={(code) =>
                onChange({ ...q, requiredFor: toggleCode(q.requiredFor ?? [], code) })
              }
            />
          )}
        </div>
      )}
      {choice && (
        <OptionsEditor
          qid={q.id}
          options={q.options ?? []}
          lang={lang}
          stale={stale}
          ai={ai}
          onChange={(options) => onChange({ ...q, options })}
        />
      )}
    </div>
  );
}

export function SectionsEditor({
  draft,
  lang,
  reserved,
  existing,
  stale,
  ai,
  onChange,
}: EditorProps) {
  const setSections = (sections: Draft["sections"]) => onChange({ ...draft, sections });
  const sectionIds = draft.sections.map((s) => s.id);
  // Which sections stand open is the admin's, by id: a moved section
  // takes its panel along instead of leaving it at the old slot.
  const [openIds, setOpenIds] = useState(() => new Set(sectionIds.slice(0, 1)));
  const setOpen = (id: string, open: boolean) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  return (
    <div className="ie-sections">
      {draft.sections.map((s, si) => {
        const holdsReserved = s.questions.some((q) => reserved.questions.includes(q.id));
        const isNew = !existing.has(`section:${s.id}`);
        const questionIdsHere = s.questions.map((q) => q.id);
        const behind = behindIn({ sections: [s] }, lang, stale);
        const setSection = (next: typeof s) =>
          setSections(draft.sections.map((x, j) => (j === si ? next : x)));
        const rename = (id: string, rest: Partial<typeof s> = {}) => {
          if (id !== s.id && openIds.has(s.id)) {
            setOpen(s.id, false);
            setOpen(id, true);
          }
          setSection({ ...s, ...rest, id });
        };
        return (
          <details
            key={rowKey(sectionIds, si)}
            className="ie-section"
            open={openIds.has(s.id)}
            onToggle={(e) => setOpen(s.id, e.currentTarget.open)}
          >
            <summary className="ie-section-head">
              <span className="ie-section-title">{s.title.en || "(untitled section)"}</span>
              <span className="cc-pill muted">
                {s.questions.length} question{s.questions.length === 1 ? "" : "s"}
              </span>
              <BehindPills behind={behind} className="cc-pill muted" />
              <code className="ie-hint">{s.id}</code>
            </summary>
            <div className="ie-section-body">
              <div className="ie-q-head">
                <IdField
                  label={`Section id (${s.id})`}
                  value={s.id}
                  locked={!isNew}
                  onChange={rename}
                />
                <RowActions
                  what={`section ${s.id}`}
                  onUp={() => setSections(move(draft.sections, si, -1))}
                  onDown={() => setSections(move(draft.sections, si, 1))}
                  onRemove={() => setSections(without(draft.sections, si))}
                  locked={
                    draft.sections.length === 1
                      ? "The form needs at least one section"
                      : holdsReserved
                        ? "It holds questions the case record reads by name"
                        : undefined
                  }
                />
              </div>
              <LabelField
                label={`Section ${s.id}`}
                text={s.title}
                lang={lang}
                stale={stale.has(`section.${s.id}`)}
                ai={ai.has(`section.${s.id}`)}
                onChange={(title) =>
                  isNew
                    ? rename(followLabel(s.id, s.title.en, title.en, "section"), { title })
                    : setSection({ ...s, title })
                }
              />
              {s.questions.map((q, qi) => (
                <QuestionEditor
                  key={rowKey(questionIdsHere, qi)}
                  q={q}
                  lang={lang}
                  draft={draft}
                  reserved={reserved}
                  existing={existing}
                  stale={stale}
                  ai={ai}
                  onChange={(next) =>
                    setSection({
                      ...s,
                      questions: s.questions.map((x, j) => (j === qi ? next : x)),
                    })
                  }
                  onMove={(dir) => setSection({ ...s, questions: move(s.questions, qi, dir) })}
                  onRemove={() => setSection({ ...s, questions: without(s.questions, qi) })}
                />
              ))}
              <button
                type="button"
                className="cc-link-btn"
                aria-label={`Add a question to ${s.id}`}
                onClick={() =>
                  setSection({ ...s, questions: [...s.questions, newQuestion(questionIds(draft))] })
                }
              >
                Add a question
              </button>
            </div>
          </details>
        );
      })}
      <button
        type="button"
        className="cc-link-btn"
        onClick={() =>
          setSections([
            ...draft.sections,
            newSection(new Set(draft.sections.map((s) => s.id)), questionIds(draft)),
          ])
        }
      >
        Add a section
      </button>
    </div>
  );
}
